import { useState } from 'react';
import { ItemScheme } from '../../../Components/Form/MagicFormItem';
import { useAppContext } from '../../../Services/AppContext';
import { MagicField } from '../../../Enums/MagicField.enum';
import usePatientAddressContainer from '../../EArchive/Hooks/usePatientAddressContainer';
import { useEffectAsync } from '../../../helpers';

const DEFAULT_FORM_SCHEME: ItemScheme[] = [
	{
		type: MagicField.WORKPLACE_SELECT,
		key: 'parentId',
		title: 'component.caseType.higherManagementUnit',
		settings: {
			required: true,
			workplaceTypes: ['main', 'folder'],
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'name',
		title: 'base.shared.workplaceName',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'codeName',
		title: 'component.workplaceSettings.shortcut',
		settings: {
			required: false,
		},
	},
	{
		type: MagicField.FIELD_OF_CARE_SUKL_SELECT,
		key: 'fieldOfCare',
		title: 'base.fieldOfCare',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.FORM_OF_CARE_SELECT,
		key: 'formOfCare',
		title: 'component.workplaceAndWorkers.formOfCare',
	},
	{
		type: MagicField.PHONE_NUMBER,
		key: 'phone',
		title: 'component.generalForm.workplacePhone',
	},
	{
		type: MagicField.CONTAINER,
		key: 'address',
		children: [],
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'icpNumber',
		title: 'component.generalForm.icp',
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'iczNumber',
		title: 'component.generalForm.icz',
	},
	{
		type: MagicField.TERM_ITEM_SELECT,
		key: 'workplaceInsuranceCompanyBranch',
		title: 'component.generalForm.insuranceCompanyBranch',
		settings: {
			termType: 'insurance_company_branch',
			labelFormat: '{value} - {name}',
			portal: true,
		},
	},
	{
		type: MagicField.CHECKBOX,
		key: 'calendarEmailNotification',
		title: 'component.generalForm.workplaceEmailNotification',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'patientProfileChangeNotification',
		title: 'component.generalForm.patientProfileChangeNotification',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'memberSearchableOutside',
		title: 'component.generalForm.memberSearchableOutsideAll',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'workplaceSearchableOutside',
		title: 'component.generalForm.workplaceSearchableOutside',
	},
	{
		type: MagicField.SELECT,
		key: 'createCaseTarget',
		title: 'component.generalForm.workplaceCaseTarget',
		children: [
			{
				key: 'WORKPLACE_MEMBER',
				value: 'WORKPLACE_MEMBER',
				title:
					'component.generalForm.workplaceCaseTargetOptions.workplaceMember',
			},
			{
				key: 'WORKPLACE',
				value: 'WORKPLACE',
				title: 'component.generalForm.workplaceCaseTargetOptions.workplace',
			},
		],
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.SELECT,
		key: 'onlineOrderTarget',
		title: 'component.generalForm.onlineOrderTarget',
		children: [
			{
				key: 'WORKPLACE_MEMBER',
				value: 'WORKPLACE_MEMBER',
				title:
					'component.generalForm.workplaceCaseTargetOptions.workplaceMember',
			},
			{
				key: 'WORKPLACE',
				value: 'WORKPLACE',
				title: 'component.generalForm.workplaceCaseTargetOptions.workplace',
			},
		],
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.CHECKBOX,
		key: 'useElectronicSignatureTimestamp',
		title: 'component.workplaceDetailComponent.electronicSignatureTimestamp',
	},
	{
		type: MagicField.TEXT,
		title:
			'component.workplaceDetailComponent.electronicSignatureTimestampNote',
		settings: {
			labelStyle: { fontSize: '12px', padding: '0 33px', display: 'none' },
		},
	},
];

const LATAM_FORM_SCHEME: ItemScheme[] = [
	{
		type: MagicField.WORKPLACE_SELECT,
		key: 'parentId',
		title: 'component.caseType.higherManagementUnit',
		settings: {
			required: true,
			workplaceTypes: ['main', 'folder'],
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'name',
		title: 'base.shared.workplaceName',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'codeName',
		title: 'component.workplaceSettings.shortcut',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.CONTAINER,
		key: 'address',
		children: [],
	},
	{
		type: MagicField.CHECKBOX,
		key: 'calendarEmailNotification',
		title: 'component.generalForm.workplaceEmailNotification',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'patientProfileChangeNotification',
		title: 'component.generalForm.patientProfileChangeNotification',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'memberSearchableOutside',
		title: 'component.generalForm.memberSearchableOutsideAll',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'workplaceSearchableOutside',
		title: 'component.generalForm.workplaceSearchableOutside',
	},
	{
		type: MagicField.SELECT,
		key: 'createCaseTarget',
		title: 'component.generalForm.workplaceCaseTarget',
		children: [
			{
				key: 'WORKPLACE_MEMBER',
				value: 'WORKPLACE_MEMBER',
				title:
					'component.generalForm.workplaceCaseTargetOptions.workplaceMember',
			},
			{
				key: 'WORKPLACE',
				value: 'WORKPLACE',
				title: 'component.generalForm.workplaceCaseTargetOptions.workplace',
			},
		],
	},
	{
		type: MagicField.SELECT,
		key: 'onlineOrderTarget',
		title: 'component.generalForm.onlineOrderTarget',
		children: [
			{
				key: 'WORKPLACE_MEMBER',
				value: 'WORKPLACE_MEMBER',
				title:
					'component.generalForm.workplaceCaseTargetOptions.workplaceMember',
			},
			{
				key: 'WORKPLACE',
				value: 'WORKPLACE',
				title: 'component.generalForm.workplaceCaseTargetOptions.workplace',
			},
		],
	},
	{
		type: MagicField.IMAGE_UPLOAD,
		key: 'logoUrl',
		title: 'base.workplaceLogo',
	},
	{
		type: MagicField.IMAGE_UPLOAD,
		key: 'letterHeadPDFUrl',
		title: 'base.letterhead',
	},
];

const SLOVAKIA_FORM_SCHEME: ItemScheme[] = [
	{
		type: MagicField.WORKPLACE_SELECT,
		key: 'parentId',
		title: 'component.caseType.higherManagementUnit',
		settings: {
			required: true,
			workplaceTypes: ['main', 'folder'],
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'name',
		title: 'base.shared.workplaceName',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.FIELD_OF_CARE_SUKL_SELECT,
		key: 'fieldOfCare',
		title: 'base.fieldOfCare',
	},
	{
		type: MagicField.PHONE_NUMBER,
		key: 'phone',
		title: 'component.generalForm.workplacePhone',
	},
	{
		type: MagicField.CONTAINER,
		key: 'address',
		children: [],
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'icpNumber',
		title: 'component.generalForm.icp',
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'iczNumber',
		title: 'component.generalForm.icz',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'calendarEmailNotification',
		title: 'component.generalForm.workplaceEmailNotification',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'patientProfileChangeNotification',
		title: 'component.generalForm.patientProfileChangeNotification',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'memberSearchableOutside',
		title: 'component.generalForm.memberSearchableOutsideAll',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'workplaceSearchableOutside',
		title: 'component.generalForm.workplaceSearchableOutside',
	},
	{
		type: MagicField.SELECT,
		key: 'createCaseTarget',
		title: 'component.generalForm.workplaceCaseTarget',
		children: [
			{
				key: 'WORKPLACE_MEMBER',
				value: 'WORKPLACE_MEMBER',
				title:
					'component.generalForm.workplaceCaseTargetOptions.workplaceMember',
			},
			{
				key: 'WORKPLACE',
				value: 'WORKPLACE',
				title: 'component.generalForm.workplaceCaseTargetOptions.workplace',
			},
		],
	},
	{
		type: MagicField.SELECT,
		key: 'onlineOrderTarget',
		title: 'component.generalForm.onlineOrderTarget',
		children: [
			{
				key: 'WORKPLACE_MEMBER',
				value: 'WORKPLACE_MEMBER',
				title:
					'component.generalForm.workplaceCaseTargetOptions.workplaceMember',
			},
			{
				key: 'WORKPLACE',
				value: 'WORKPLACE',
				title: 'component.generalForm.workplaceCaseTargetOptions.workplace',
			},
		],
	},
];

const HUNGARY_FORM_SCHEME: ItemScheme[] = [
	{
		type: MagicField.WORKPLACE_SELECT,
		key: 'parentId',
		title: 'component.caseType.higherManagementUnit',
		settings: {
			required: true,
			workplaceTypes: ['main', 'folder'],
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'name',
		title: 'base.shared.workplaceName',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'codeName',
		title: 'component.workplaceSettings.shortcut',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.PHONE_NUMBER,
		key: 'phone',
		title: 'component.generalForm.workplacePhone',
	},
	{
		type: MagicField.CONTAINER,
		key: 'address',
		children: [],
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'icpNumber',
		title: 'component.generalForm.icp',
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'iczNumber',
		title: 'component.generalForm.icz',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'calendarEmailNotification',
		title: 'component.generalForm.workplaceEmailNotification',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'patientProfileChangeNotification',
		title: 'component.generalForm.patientProfileChangeNotification',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'memberSearchableOutside',
		title: 'component.generalForm.memberSearchableOutsideAll',
	},
	{
		type: MagicField.CHECKBOX,
		key: 'workplaceSearchableOutside',
		title: 'component.generalForm.workplaceSearchableOutside',
	},
	{
		type: MagicField.SELECT,
		key: 'createCaseTarget',
		title: 'component.generalForm.workplaceCaseTarget',
		children: [
			{
				key: 'WORKPLACE_MEMBER',
				value: 'WORKPLACE_MEMBER',
				title:
					'component.generalForm.workplaceCaseTargetOptions.workplaceMember',
			},
			{
				key: 'WORKPLACE',
				value: 'WORKPLACE',
				title: 'component.generalForm.workplaceCaseTargetOptions.workplace',
			},
		],
	},
	{
		type: MagicField.SELECT,
		key: 'onlineOrderTarget',
		title: 'component.generalForm.onlineOrderTarget',
		children: [
			{
				key: 'WORKPLACE_MEMBER',
				value: 'WORKPLACE_MEMBER',
				title:
					'component.generalForm.workplaceCaseTargetOptions.workplaceMember',
			},
			{
				key: 'WORKPLACE',
				value: 'WORKPLACE',
				title: 'component.generalForm.workplaceCaseTargetOptions.workplace',
			},
		],
	},
];

const polandExcludedKeys = ['fieldOfCare', 'formOfCare'];

const POLAND_FORM_SCHEME: ItemScheme[] = [
	...DEFAULT_FORM_SCHEME.filter(
		item => item.key && !polandExcludedKeys.includes(item.key),
	),
	{
		type: MagicField.TEXT_FIELD,
		key: 'workplaceP1Region',
		title: 'component.workplaceDetailComponent.p1Region',
		settings: {
			showCharCounter: true,
			rules: [
				{
					type: 'maxLength',
					value: 14,
					message: 'validation.workplaceDetailComponent.p1Region',
				},
				{
					type: 'min',
					value: 14,
					message: 'validation.workplaceDetailComponent.p1Region',
				},
			],
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'workplaceP1Oid',
		title: 'component.workplaceDetailComponent.p1Oid',
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'workplaceP1Extension',
		title: 'component.workplaceDetailComponent.p1Extension',
	},
];

export default function useBasicData() {
	const { globalTemplate } = useAppContext();
	const { addToAddressContainer, isLoading } = usePatientAddressContainer();
	const [formScheme, setFormScheme] = useState<ItemScheme[]>([]);

	const getSchemeByCountry = (): ItemScheme[] => {
		switch (globalTemplate) {
			case 'peru':
			case 'ecuador':
			case 'mexico':
			case 'colombia':
				return LATAM_FORM_SCHEME;
			case 'slovakia':
				return SLOVAKIA_FORM_SCHEME;
			case 'hungary':
				return HUNGARY_FORM_SCHEME;
			case 'poland':
				return POLAND_FORM_SCHEME;
			default:
				return DEFAULT_FORM_SCHEME;
		}
	};

	useEffectAsync(async () => {
		const scheme = getSchemeByCountry();
		const schemeWithAddress = await addToAddressContainer(scheme);
		setFormScheme(schemeWithAddress);
	}, []);

	return { formScheme, isLoading };
}
